import { ErrorOutlined, Refresh } from '@mui/icons-material';
import { Alert, Box, Button, Container, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDialog?: boolean;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  eventId: string | null;
}

/**
 * Sentry-integrated Error Boundary with Material-UI fallback UI
 * Automatically captures errors and provides user-friendly error display
 */
export class SentryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, eventId: null };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true, eventId: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Capture the error with Sentry and get the event ID
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorBoundary: true,
      },
    });

    this.setState({ eventId });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by SentryErrorBoundary:', error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, eventId: null });
    this.props.onReset?.();
  };

  handleReportFeedback = (): void => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default Material-UI error UI
      return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 3,
            }}
          >
            <ErrorOutlined color="error" sx={{ fontSize: 64 }} />

            <Typography variant="h4" component="h1" gutterBottom>
              Oops! Something went wrong
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mb: 2,
              }}
            >
              We&apos;re sorry for the inconvenience. The error has been
              automatically reported to our team for investigation.
            </Typography>

            {this.state.eventId && (
              <Alert severity="info" sx={{ width: '100%', maxWidth: 500 }}>
                <Typography variant="body2">
                  Error ID: <code>{this.state.eventId}</code>
                </Typography>
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<Refresh />}
                onClick={this.handleReset}
              >
                Try Again
              </Button>

              {/* SpecRef: ARCH-005 */}
              {/* Reference: docs/decisions/ARCH-005-ui-error-detection-resilience.md#8-kiosk-mode--suppress-sentry-report-dialog */}
              {this.state.eventId && this.props.showDialog !== false && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={this.handleReportFeedback}
                >
                  Report Issue
                </Button>
              )}

              <Button variant="text" onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </Box>

            {process.env.NODE_ENV === 'development' && (
              <Alert
                severity="warning"
                sx={{ width: '100%', maxWidth: 500, mt: 2 }}
              >
                <Typography variant="body2">
                  <strong>Development Mode:</strong> Check the console for
                  detailed error information.
                </Typography>
              </Alert>
            )}
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

// HOC wrapper for easier usage
export const withSentryErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<Props, 'children'>,
) => {
  const WrappedComponent = (props: P) => (
    <SentryErrorBoundary {...options}>
      <Component {...props} />
    </SentryErrorBoundary>
  );

  WrappedComponent.displayName = `withSentryErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

// Sentry's built-in error boundary with our custom fallback
export const SentryErrorBoundaryWithFallback = Sentry.withErrorBoundary(
  ({ children }: { children: ReactNode }) => <>{children}</>,
  {
    fallback: ({ resetError }) => (
      <SentryErrorBoundary fallback={undefined} onReset={resetError}>
        {null}
      </SentryErrorBoundary>
    ),
    beforeCapture: (scope, error, errorInfo) => {
      scope.setTag('errorBoundary', 'sentry-built-in');
      scope.setContext('errorInfo', {
        errorInfo,
      });
    },
  },
);
